import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Plus, X, Save, Trash2 } from "lucide-react"; // Íconos de lucide-react

export default function ModalSeasonalHours({
  isModalOpen,
  setIsModalOpen,
  onSaveSeasonalHours,
  disabledDays,
  errorMessage,
  errorMessageOverLap,
  editingSeason, // Datos del horario estacional que se está editando
}) {
  const [seasonName, setSeasonName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [schedule, setSchedule] = useState({
    lun: [{ start: "09:00", end: "17:00" }],
    mar: [{ start: "09:00", end: "17:00" }],
    mie: [{ start: "09:00", end: "17:00" }],
    jue: [{ start: "09:00", end: "17:00" }],
    vie: [{ start: "09:00", end: "17:00" }],
    sab: [{ start: "09:00", end: "13:15" }],
    dom: [],
  });

  // Cargar los datos de edición en los estados cuando el modal se abre
  useEffect(() => {
    if (isModalOpen && editingSeason) {
      setSeasonName(editingSeason.seasonName);
      setStartDate(editingSeason.startDate);
      setEndDate(editingSeason.endDate);
      setSchedule(editingSeason.schedule);
    }
  }, [isModalOpen, editingSeason]);

  // Reiniciar el formulario solo cuando el modal se cierra y no estamos en modo de edición
  useEffect(() => {
    if (!isModalOpen && !editingSeason) {
      resetForm();
    }
  }, [isModalOpen, editingSeason]);

  const resetForm = () => {
    setSeasonName("");
    setStartDate("");
    setEndDate("");
    setSchedule({
      lun: [{ start: "09:00", end: "17:00" }],
      mar: [{ start: "09:00", end: "17:00" }],
      mie: [{ start: "09:00", end: "17:00" }],
      jue: [{ start: "09:00", end: "17:00" }],
      vie: [{ start: "09:00", end: "17:00" }],
      sab: [{ start: "09:00", end: "13:15" }],
      dom: [],
    });
  };

  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const isOverlapping = (block1, block2) => {
    const start1 = timeToMinutes(block1.start);
    const end1 = timeToMinutes(block1.end);
    const start2 = timeToMinutes(block2.start);
    const end2 = timeToMinutes(block2.end);

    return start1 < end2 && end1 > start2;
  };

  const handleTimeChange = (day, index, field, value) => {
    const updatedSchedule = { ...schedule };
    const updatedBlock = { ...updatedSchedule[day][index], [field]: value };

    const isOverlap = updatedSchedule[day].some((block, i) => {
      if (i === index) return false; // Excluir el bloque actual
      return isOverlapping(block, updatedBlock);
    });

    if (isOverlap) {
      errorMessageOverLap();
      return;
    }

    updatedSchedule[day][index] = updatedBlock;
    setSchedule(updatedSchedule);
  };

  const addTimeBlock = (day) => {
    const lastBlock = schedule[day][schedule[day].length - 1]; // Obtener el último bloque del día
    const defaultStart = lastBlock ? lastBlock.end : "09:00"; // Si hay un último bloque, comenzar después de su hora de fin
    const defaultEnd = "17:00"; // Hora de fin predeterminada

    const newBlock = { start: defaultStart, end: defaultEnd };

    const isOverlap = schedule[day].some((block) => {
      return isOverlapping(block, newBlock);
    });

    if (isOverlap) {
      errorMessageOverLap();
      return;
    }

    const updatedSchedule = { ...schedule };
    updatedSchedule[day].push(newBlock);
    setSchedule(updatedSchedule);
  };

  const removeTimeBlock = (day, index) => {
    const updatedSchedule = { ...schedule };
    updatedSchedule[day].splice(index, 1);
    setSchedule(updatedSchedule);
  };

  const toggleDayAvailability = (day) => {
    const updatedSchedule = { ...schedule };
    if (updatedSchedule[day].length === 0) {
      updatedSchedule[day] = [{ start: "09:00", end: "17:00" }];
    } else {
      updatedSchedule[day] = [];
    }
    setSchedule(updatedSchedule);
  };

  const handleSave = () => {
    // Validar si alguna fecha dentro del rango está deshabilitada
    const start = new Date(startDate);
    const end = new Date(endDate);
    for (let d = start; d <= end; d.setDate(d.getDate() + 1)) {
      const dateString = d.toISOString().split("T")[0];
      if (disabledDays.includes(dateString)) {
        errorMessage();
        return;
      }
    }

    onSaveSeasonalHours({ seasonName, startDate, endDate, schedule });
    setIsModalOpen(false);
    resetForm(); // Reiniciar el formulario después de guardar
  };

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          key="modal-background"
          onClick={() => setIsModalOpen(false)}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-20 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <motion.div
            key="modal-content"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="bg-background rounded-lg shadow-xl p-6 font-sans w-full max-w-lg mx-4 border border-gray-200 flex flex-col max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-xl font-semibold mb-4 text-text">Edit Seasonal Hours</h2>

            {/* Campo para el nombre de la temporada */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">Nombre de la temporada</label>
              <input
                type="text"
                value={seasonName}
                onChange={(e) => setSeasonName(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 w-full text-sm"
                placeholder="Ej: Verano, Vacaciones, etc."
              />
            </div>

            {/* Selector de fechas */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                min={new Date().toISOString().split("T")[0]} // Fecha mínima: hoy
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 w-full text-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                min={startDate} // Fecha mínima: la fecha de inicio
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 w-full text-sm"
              />
            </div>

            {/* Lista de días y horarios */}
            <div className="space-y-3">
              {Object.entries(schedule).map(([day, timeBlocks]) => (
                <div key={day} className="border-b border-gray-100 pb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-subtitle">{day.toUpperCase()}</span>
                    <button
                      onClick={() => addTimeBlock(day)}
                      className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded-full"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>

                  {timeBlocks.length > 0 ? (
                    timeBlocks.map((block, index) => (
                      <div key={index} className="flex items-center gap-2 mb-2">
                        <input
                          type="time"
                          value={block.start}
                          onChange={(e) => handleTimeChange(day, index, "start", e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 w-full text-sm"
                        />
                        <span className="text-gray-400">-</span>
                        <input
                          type="time"
                          value={block.end}
                          onChange={(e) => handleTimeChange(day, index, "end", e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 w-full text-sm"
                        />
                        <button
                          onClick={() => removeTimeBlock(day, index)}
                          className="text-gray-400 hover:text-red-500 transition-colors p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">Unavailable</span>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors w-full sm:w-auto flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-full sm:w-auto flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}